import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";

// ---------------------------------------------------------------- flags

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  category: string;
  enabled: boolean;
  enabledForRoles: string[];
  enabledForUsers: string[];
  percentage: number;
  expiresAt?: string | null;
  /** Referenced by platform code: cannot be deleted. */
  known: boolean;
  updatedAt: string;
}

export interface FlagInput {
  key?: string;
  name: string;
  description?: string | null;
  category: string;
  enabled: boolean;
  enabledForRoles: string[];
  enabledForUsers: string[];
  percentage: number;
  expiresAt?: string | null;
}

export const FLAG_CATEGORIES = ["portal", "backoffice", "console", "agents", "automations", "integrations", "identity", "experiments", "general"];

export function useFeatureFlags() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "flags"], queryFn: () => api.get<FeatureFlag[]>("/api/platform/flags"), refetchInterval: 30_000 });
}

export function useFlagActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["platform", "flags"] });
    void qc.invalidateQueries({ queryKey: ["platform-overview"] });
  };
  return {
    create: useSave({ mutationFn: (input: FlagInput) => api.post<FeatureFlag>("/api/platform/flags", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { key: string } & FlagInput) => api.put<FeatureFlag>(`/api/platform/flags/${input.key}`, input), onSuccess: invalidate }),
    toggle: useSave({ mutationFn: (input: { key: string; enabled: boolean }) => api.post<FeatureFlag>(`/api/platform/flags/${input.key}/toggle`, { enabled: input.enabled }), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(`/api/platform/flags/${key}`), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- secrets

export interface SecretSummary {
  id: string;
  key: string;
  description?: string | null;
  hint: string;
  hasValue: boolean;
  environmentVariable?: string | null;
  environmentVariablePresent: boolean;
  usedBy: string[];
  rotatedByName?: string | null;
  rotatedAt?: string | null;
  expiresAt?: string | null;
  expired: boolean;
  updatedAt: string;
  reference: string;
}

export interface SecretsResponse { secrets: SecretSummary[]; dangling: Array<{ key: string; usedBy: string[] }> }

export function useSecrets() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "secrets"], queryFn: () => api.get<SecretsResponse>("/api/platform/secrets"), refetchInterval: 60_000 });
}

export function useSecretActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["platform", "secrets"] });
  return {
    set: useSave({ mutationFn: (input: { key: string; value?: string | null; description?: string | null; expiresAt?: string | null; environmentVariable?: string | null }) => api.put<SecretSummary>(`/api/platform/secrets/${input.key}`, input), onSuccess: invalidate }),
    /** Reads the secret to check it resolves; stores nothing, so its failure is not headed "Not saved". */
    test: useSave({ mutationFn: (key: string) => api.post<{ ok: boolean; message: string; length: number }>(`/api/platform/secrets/${key}/test`, {}), failed: t("admin:common.error") }),
    remove: useSave({ mutationFn: (input: { key: string; force?: boolean }) => api.delete<void>(`/api/platform/secrets/${input.key}${input.force ? "?force=true" : ""}`), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- settings

export type SettingValueType = "String" | "Number" | "Boolean" | "Json" | "Secret" | "Select" | "Duration";

export interface SettingGroupInfo { key: string; name: string; nameAr: string; description: string; descriptionAr: string; icon: string; order: number }

export interface SettingValue {
  key: string;
  group: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  valueType: SettingValueType;
  value: unknown;
  defaultValue: unknown;
  isDefault: boolean;
  options?: unknown;
  requiresRelease: boolean;
  requiresRestart: boolean;
  advanced: boolean;
  sortOrder: number;
  updatedAt?: string | null;
}

export interface SettingGroup { group: SettingGroupInfo; settings: SettingValue[]; overridden: number }

export interface SettingWriteResult { setting: SettingValue; applied: boolean; requiresRelease: boolean; changeItemValueJson?: string | null }

export function useSettingGroups() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "settings"], queryFn: () => api.get<SettingGroup[]>("/api/platform/settings?advanced=true"), staleTime: 15_000 });
}

export function useSettingActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["platform", "settings"] });
  return {
    set: useSave({ mutationFn: (input: { key: string; value: unknown }) => api.put<SettingWriteResult>(`/api/platform/settings/${input.key}`, { value: input.value }), onSuccess: invalidate }),
    reset: useSave({ mutationFn: (key: string) => api.delete<SettingValue>(`/api/platform/settings/${key}`), onSuccess: invalidate }),
  };
}

/** The decision controls in force (setting procedure.decisionControls): what the test-environment controls relax, when they are in force. */
export interface DecisionControlsView { key: string; value: "Strict" | "TestEnvironment"; relaxed: boolean; available: boolean; isDefault: boolean; default: string; relaxations: Array<{ code: string; en: string; ar: string }> }

export function useDecisionControls() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "settings", "decision-controls"], queryFn: () => api.get<DecisionControlsView>("/api/platform/decision-controls"), staleTime: 15_000 });
}

/** Select options as declared in the definition's OptionsJson. */
export function selectOptions(s: SettingValue): Array<{ value: string; label: string; labelAr?: string }> {
  return Array.isArray(s.options) ? (s.options as Array<{ value: string; label: string; labelAr?: string }>) : [];
}

/** Numeric constraints ({ min, max, step, unit }) as declared in OptionsJson. */
export function numberRules(s: SettingValue): { min?: number; max?: number; step?: number; unit?: string } {
  return s.options && typeof s.options === "object" && !Array.isArray(s.options) ? (s.options as { min?: number; max?: number; step?: number; unit?: string }) : {};
}
